import { inject, Injectable, OnInit, signal, WritableSignal } from '@angular/core';
import { AuthenticationResponse, AuthResponse } from './models/auth/AuthResponse.model';
import { UserLogin } from './models/auth/UserLogin.model';
import { JWT } from './models/auth/jwt.model';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { Store } from '@ngrx/store';
import { UserActions } from './stores/actions/user.actions';
import { CoreActions } from './stores/actions/core.actions';
import { UserComplete } from './models/account/user-complete.model';
import { SocialAuthService } from '@abacritt/angularx-social-login';
import { UserRegistration } from './models/auth/user-registration.model';
import { EventBasic } from './models/events/event-basic.model';
import { ApiWrapperService } from 'ngx-apiwrapper';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly _apiWrapper = inject(ApiWrapperService);
  private readonly _router: Router = inject(Router);
  private readonly _messageService: MessageService = inject(MessageService);
  private readonly _store: Store = inject(Store);
  //private readonly _socialAuthService: SocialAuthService = inject(SocialAuthService);

  private authChangeSub = new Subject<boolean>()
  public authChanged = this.authChangeSub.asObservable();
  isLoggedIn: boolean = this.checkTokenExpiry();

  authStateChanged$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  currentToken$: BehaviorSubject<JWT | null> = new BehaviorSubject<JWT | null>(null);
  
  get currentToken(): JWT | null{
    let storedToken = localStorage.getItem("token");
    if (storedToken) {
       return JSON.parse(atob(localStorage.getItem("token")!.split('.')[1]))
    }
    return null;
  }

  get currentAuthToken(): string | null {
    let storedToken = localStorage.getItem("token");
    if (storedToken) {
      //this._store.dispatch(UserActions.loadUser());
      return storedToken;
    }
    return null;
  }

  public sendAuthStateChangeNotification = (isAuthenticated: boolean) => 
    this.authStateChanged$.next(isAuthenticated);

  login(loginDetails: UserLogin) {
    this._apiWrapper.post<AuthResponse>('auth/login', loginDetails).subscribe(
      response => {
        if (response.status == AuthenticationResponse.Successful) {
          if (environment.preview){
            var token: JWT = JSON.parse(atob(response.token.split('.')[1]))
            if (!token.Permissions.some(x => x === 'Preview')){
              this._messageService.add({severity:'error', detail:'You do not ahve permission to access the preview version of LRP Tickets', summary:'Login Error'})
              return;
            }
            this._router.navigate(['/'])
          }
          localStorage.setItem("token", response.token);

          this.currentToken$.next(this.currentToken)
          this.authStateChanged$.next(true);
          this.sendAuthStateChangeNotification(true);
          return this._store.dispatch(CoreActions.loginRedirectURL());
        }
        if (response.status == AuthenticationResponse.UsernameOrPasswordError) {
          this._messageService.add({severity:'error', detail:'Unknown e-mail address or incorrect password', summary:'Login Error'})
        }
      }
    )
  }

  get isPreviewUser(): boolean {
    if (this.currentToken){
      return this.currentToken?.Permissions?.includes('Preview') ?? false;
    }
    return false;
  }

  requestPasswordReset(email: string) : Observable<boolean> {
    return this._apiWrapper.post('auth/RequestPasswordReset', { emailAddress: email })
  }

  passwordReset(email: string, code: string, password: string) {
    this._apiWrapper.post<UserComplete>('auth/ResetPassword', { emailAddress: email, code: code, password: password }).subscribe(
      response => {
        if (response) {
          this._router.navigateByUrl('auth/login');
        }
      }
    )
  }

  register(UserRegistration: UserRegistration) {
    this._apiWrapper.post<AuthResponse>('auth/register', UserRegistration).subscribe(
      response => {
        if (response.status == AuthenticationResponse.Successful) {
          localStorage.setItem("token", response.token);
          this.sendAuthStateChangeNotification(true);
          return this._store.dispatch(CoreActions.loginRedirectURL());
        }
        if (response.status == AuthenticationResponse.UsernameOrPasswordError) {
          this._messageService.add({ severity: 'error', detail: `We're sorry, that e-mail address is already in use!` })
        }
      }
    );
  }

  socialLogin(user: UserLogin) {
    this._apiWrapper.post<AuthResponse>('auth/social', user).subscribe(response => {
      if (response.status == AuthenticationResponse.Successful) {
        this.sendAuthStateChangeNotification(true);
        localStorage.setItem("token", response.token);
        return this._store.dispatch(CoreActions.loginRedirectURL());
      }
      if (response.status == AuthenticationResponse.UsernameOrPasswordError) {
        this._messageService.add({severity:'error', detail:'Unknown e-mail address or incorrect password', summary:'Login Error'})
      }
    })
  }

  logout = () => {
    localStorage.removeItem('token');
    this.authStateChanged$.next(false);
    this._store.dispatch(UserActions.logoutUser());
    //this._socialAuthService.signOut();
    this._router.navigateByUrl('/');
  }

  checkJWTTokenExpiry(token: JWT): boolean {
    return new Date(token.exp * 1000) > new Date();
  }

  checkTokenExpiry(token?: string ) : boolean{
    let lookupToken: JWT;

    if (token)
    {
       lookupToken = JSON.parse(atob(token.split('.')[1]));
    }
    else {
      const storedToken = localStorage.getItem("token");
      if (storedToken) {
        lookupToken = JSON.parse(atob(storedToken.split('.')[1]));
      }
      else {
        return false;
      }
    }
    return new Date(lookupToken.exp * 1000) > new Date();
  }

  hasValidToken(): boolean {
    const token = this.currentToken;
    if (token) {
      return new Date(token.exp * 1000) > new Date();
    }
    return false;
  }

  getAuthRole(): string {
    const token = this.currentToken;
    if (token) {
      return token.Role
    }
    return '';
  }

  canAccessAdmin(): boolean {
    const token = this.currentToken
    if (token && this.checkTokenExpiry()) {
      var adminRoles = new Set(['eventHost', 'systemAdmin', 'Admin', 'Super Admin','Organiser']);
      if (typeof token.Permissions === 'string') {
        return adminRoles.has(token.Permissions)
      }
      else {
        return token.Permissions?.some(x => adminRoles.has(x));
      }
    }
    return false;
  }

  isAdministrator(): boolean {
    const token = this.currentToken
    if (token) {
      var adminRoles = new Set(['Admin', 'Super Admin']);
      if (typeof token.Permissions === 'string') {
        return adminRoles.has(token.Permissions)
      }
      else {
        return token.Permissions?.some(x => adminRoles.has(x));
      }
    }
    return false;
  }

  getEventHosts(): number[] | undefined {
    const token = this.currentToken;
    if (token) {
      return token.EventHosts;
    }
    return [];
  }

  getEventSystems(): number[] | undefined {
    const token = this.currentToken;
    if (token) {
      return token.EventSystems;
    }
    return [];
  }

  getParentEventSystems():number[] | undefined{
    const token = this.currentToken;
    if (token){
      return token.ParentEventSystems;
    }
    return [];
  }

  isEventAdmin(lrpEvent?: EventBasic): boolean {
    const token = this.currentToken
    if (token && lrpEvent){
      const eventSystems: number[] | undefined = token.EventSystems;
      const eventHosts: number[] | undefined = token.EventHosts;
      const parentEventSystems: number[] | undefined = token.ParentEventSystems;
      if (token.Permissions.some(x => ['Admin','Super Admin'].includes(x)))
      {
        return true;
      }
      if (['eventHost','systemAdmin'].includes(token.Role)){
        return (eventSystems?.includes(lrpEvent.eventHost.eventSystem.systemID) || eventHosts?.includes(lrpEvent.eventHost.hostID) || parentEventSystems?.includes(lrpEvent.eventHost.eventSystem.systemID)) ?? false
      }
      return false;
    }
    return false;
  }
}
